import { useId, type ReactNode, type Ref } from 'react';
import { Link } from 'react-router-dom';
import { Check, PackageOpen } from 'lucide-react';
import { EmptyState } from '@/components/EmptyState';
import { Button } from '@/components/ui/button';
import { buttonVariants } from '@/components/ui/button-variants';
import { effectiveSecurityBlocks } from '@/lib/deployPlan';
import type { Catalog, CatalogStack } from '@/types/catalog';
import { cn } from '@/lib/utils';

export interface SourceStepProps {
  catalogs: Catalog[];
  stacks: CatalogStack[];
  stacksLoading: boolean;
  stacksError: string | null;
  onRetryStacks: () => void;
  catalogId: number | null;
  stackName: string;
  onSelectCatalog: (catalogId: number) => void;
  onSelectStack: (stackName: string) => void;
  /** Lets the page move focus to the heading when the step becomes active. */
  headingRef?: Ref<HTMLHeadingElement>;
}

// Radio card: the native input is the `peer`; the face and check icon are its siblings.
const FACE =
  'flex h-full flex-col gap-2 rounded-lg border bg-card p-4 text-card-foreground transition-colors ' +
  'hover:bg-accent/50 peer-checked:border-primary peer-checked:bg-accent peer-checked:hover:bg-accent ' +
  'peer-focus-visible:ring-2 peer-focus-visible:ring-ring';
const CHECK = 'pointer-events-none absolute right-3 top-3 size-4 text-primary opacity-0 peer-checked:opacity-100';
const CARD = 'group/radio relative block cursor-pointer has-[:disabled]:cursor-not-allowed';

const Chip = ({ children }: { children: ReactNode }) => (
  <span className="rounded-md border bg-muted/40 px-2 py-0.5 text-xs tabular-nums">{children}</span>
);

function SkeletonRows() {
  return (
    <div>
      <p role="status" className="sr-only">
        Loading stacks
      </p>
      <div aria-hidden className="grid gap-3 sm:grid-cols-2">
        {[0, 1].map((i) => (
          <div key={i} className="h-24 animate-pulse rounded-lg border bg-muted/40" />
        ))}
      </div>
    </div>
  );
}

/** Use as the first wizard step: pick the catalog, then one of its stacks. */
export function SourceStep({
  catalogs,
  stacks,
  stacksLoading,
  stacksError,
  onRetryStacks,
  catalogId,
  stackName,
  onSelectCatalog,
  onSelectStack,
  headingRef,
}: SourceStepProps) {
  const uid = useId();

  return (
    <div className="space-y-6">
      <h2 ref={headingRef} tabIndex={-1} className="text-lg font-semibold outline-none">
        Choose what to deploy
      </h2>

      {catalogs.length === 0 ? (
        <EmptyState
          icon={PackageOpen}
          as="h3"
          title="No active catalogs"
          description="Create a catalog with at least one stack before deploying an app."
          action={
            <Link
              to="/catalog"
              className={cn(buttonVariants(), 'outline-none focus-visible:ring-2 focus-visible:ring-ring')}
            >
              Go to Catalog
            </Link>
          }
        />
      ) : (
        <fieldset role="radiogroup" className="min-w-0 space-y-3">
          <legend className="mb-3 text-sm font-medium">Catalog</legend>
          <div className="grid gap-3 sm:grid-cols-2">
            {catalogs.map((c) => (
              <label key={c.id} className={CARD}>
                <input
                  type="radio"
                  name={`${uid}-catalog`}
                  value={c.id}
                  checked={catalogId === c.id}
                  onChange={() => onSelectCatalog(c.id)}
                  aria-labelledby={`${uid}-catalog-${c.id}`}
                  className="peer sr-only"
                />
                <div className={FACE}>
                  <span id={`${uid}-catalog-${c.id}`} className="block truncate pr-6 font-medium" title={c.name}>
                    {c.name}
                  </span>
                  {c.description && (
                    <span className="line-clamp-2 text-sm text-muted-foreground" title={c.description}>
                      {c.description}
                    </span>
                  )}
                  <span className="mt-auto flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
                    <span className="tabular-nums">
                      {c.stacks_count} {c.stacks_count === 1 ? 'stack' : 'stacks'}
                    </span>
                    {c.expose_port > 0 && <span>Gets its own subdomain</span>}
                  </span>
                </div>
                <Check className={CHECK} aria-hidden />
              </label>
            ))}
          </div>
        </fieldset>
      )}

      {catalogId !== null && catalogs.length > 0 && (
        <fieldset role="radiogroup" className="min-w-0 space-y-3">
          <legend className="mb-3 text-sm font-medium">Stack</legend>
          {stacksLoading ? (
            <SkeletonRows />
          ) : stacksError !== null ? (
            <div
              role="alert"
              className="flex items-center justify-between gap-2 rounded-md border border-destructive/50 p-3 text-sm text-destructive"
            >
              <span>Could not load stacks: {stacksError}</span>
              <Button type="button" variant="outline" size="sm" onClick={onRetryStacks}>
                Retry
              </Button>
            </div>
          ) : stacks.length === 0 ? (
            <EmptyState as="h3" title="This catalog has no active stacks" className="py-8" />
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              {stacks.map((s) => {
                const overlays = effectiveSecurityBlocks(s).filter((b) => b.enabled).length;
                return (
                  <label key={s.id} className={CARD}>
                    <input
                      type="radio"
                      name={`${uid}-stack`}
                      value={s.name}
                      checked={stackName === s.name}
                      onChange={() => onSelectStack(s.name)}
                      aria-labelledby={`${uid}-stack-${s.id}`}
                      className="peer sr-only"
                    />
                    <div className={FACE}>
                      <span id={`${uid}-stack-${s.id}`} className="block truncate pr-6 font-medium" title={s.name}>
                        {s.name}
                      </span>
                      <span className="flex flex-wrap gap-1.5">
                        <Chip>{s.cpu_limit} cores</Chip>
                        <Chip>{s.memory_mb} MB</Chip>
                        <Chip>{s.storage_gb} GB</Chip>
                      </span>
                      {(s.shared_cpu || overlays > 0) && (
                        <span className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                          {s.shared_cpu && (
                            <span className="rounded-md bg-status-info-soft px-2 py-0.5 text-status-info">Shared CPU</span>
                          )}
                          {overlays > 0 && (
                            <span className="tabular-nums">
                              {overlays} security {overlays === 1 ? 'overlay' : 'overlays'}
                            </span>
                          )}
                        </span>
                      )}
                    </div>
                    <Check className={CHECK} aria-hidden />
                  </label>
                );
              })}
            </div>
          )}
        </fieldset>
      )}
    </div>
  );
}
